import clsx from 'clsx';
import {useEffect, useState} from 'react';
import {BlogHeading} from '@app/blog/use-public-blog';

interface BlogArticleTocProps {
  headings: BlogHeading[];
}

const SCROLL_OFFSET = 100;

function getActiveHeadingId(headings: BlogHeading[]): string | null {
  if (headings.length === 0) {
    return null;
  }

  let activeId = headings[0].id;
  for (const heading of headings) {
    const el = document.getElementById(heading.id);
    if (!el) {
      continue;
    }
    if (el.getBoundingClientRect().top <= SCROLL_OFFSET) {
      activeId = heading.id;
    }
  }

  return activeId;
}

export function BlogArticleToc({headings}: BlogArticleTocProps) {
  const [activeId, setActiveId] = useState<string | null>(
    () => headings[0]?.id ?? null,
  );

  useEffect(() => {
    const updateActiveHeading = () => {
      setActiveId(getActiveHeadingId(headings));
    };

    updateActiveHeading();
    window.addEventListener('scroll', updateActiveHeading, {passive: true});
    window.addEventListener('resize', updateActiveHeading, {passive: true});

    return () => {
      window.removeEventListener('scroll', updateActiveHeading);
      window.removeEventListener('resize', updateActiveHeading);
    };
  }, [headings]);

  if (headings.length === 0) {
    return null;
  }

  return (
    <nav className="blog-article-toc">
      <ul className="blog-article-toc-list">
        {headings.map(heading => (
          <li key={heading.id}>
            <a
              href={`#${heading.id}`}
              className={clsx(
                'blog-article-toc-link',
                activeId === heading.id && 'is-active',
              )}
              aria-current={activeId === heading.id ? 'location' : undefined}
              onClick={e => {
                e.preventDefault();
                document
                  .getElementById(heading.id)
                  ?.scrollIntoView({behavior: 'smooth', block: 'start'});
                window.history.replaceState(null, '', `#${heading.id}`);
              }}
            >
              {heading.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
